/**
 * NDP61 Inference Check — cross-device MobileNet determinism test.
 *
 * Loads the test-image manifest, runs every image through the same
 * MobileNetV2 path the check-in flow uses, and reports the resulting
 * 1280-dim embedding per image plus a fingerprint of the device. The
 * report is presented on-screen and exposed as a JSON blob the tester
 * can copy to clipboard or download — so the team can collect outputs
 * across devices/browsers and diff them offline.
 *
 * Why no server submit: keeping the tool 100% static means it can be
 * deployed behind any CDN, and the JSON payload it produces is small
 * enough to paste into a Slack thread. We can add an upload endpoint
 * later if manual collection becomes painful.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BACKEND_VALUES,
  cosineSimilarity,
  embedImage,
  embedVideoFrame,
  getBackendName,
  loadModel,
  sha256Hex,
  summariseEmbedding,
  type Backend,
  type EmbeddingSummary,
} from "./lib/inference";
import {
  readDeviceFingerprint,
  shortDeviceLabel,
  type DeviceFingerprint,
} from "./lib/device";
import { fetchManifest, type TestImage } from "./lib/manifest";
import { CameraSnapshot } from "./screens/CameraSnapshot";

interface ImageResult {
  id: string;
  url: string;
  category: TestImage["category"];
  notes?: string;
  /** SHA-256 of the bytes that were fed into MobileNet for this row. The
   *  same ArrayBuffer is used for both the digest and the inference, so
   *  this hash is a reliable proof of the inference input. */
  bytes: { length: number; sha256: string };
  embedding: EmbeddingSummary;
  /** Max cosine sim vs the POI's reference set, if `referenceEmbeddingsUrl`
   *  was set in the manifest. Null when unset or on failure. */
  refMaxCosine: number | null;
  refLoadedCount: number | null;
  /** Failure to fetch the source image bytes. */
  fetchError: string | null;
  /** Failure inside model.infer / image decode for this row. */
  inferError: string | null;
  /** Failure to fetch/parse the reference embeddings JSON, or to compute
   *  cosine against them. NEVER populated by image-fetch or inference
   *  failures — those use fetchError / inferError. */
  refError: string | null;
  durationMs: number;
}

interface RunReport {
  schema: "ndp61-inference-check@1";
  runId: string;
  startedAt: string;
  durationMs: number;
  device: DeviceFingerprint;
  deviceLabel: string;
  /** Backend the run was *asked* to use — set via `?backend=` URL param
   *  or the default (`webgl`). Recorded so a reader can attribute a
   *  divergent result to a forced backend rather than guess from the
   *  effective backend alone. */
  backendRequested: Backend;
  /** Backend TF.js actually ran on. Usually matches `backendRequested`
   *  but can differ if the requested one failed to initialise (e.g.
   *  WebGL unavailable in a locked-down WebView) and TF.js fell back. */
  tfBackend: string;
  /** What the runtime detector would auto-pick on this device today
   *  (see PR #332): "wasm" on Mali GPUs, "webgl" everywhere else. Lets
   *  the tester confirm in one glance whether a Mali phone matches the
   *  expected production override. */
  runtimeWouldPick: Backend;
  modelLoadMs: number;
  results: ImageResult[];
  /** Pairwise cosine between every pair of result embeddings. Filled in
   *  client-side so the reader can see "image A is at cosine X to image
   *  B on this device" without re-computing from the raw vectors. */
  pairwiseCosine: { a: string; b: string; cosine: number }[];
}

/** Parse `?backend=wasm|webgl|cpu` from the URL. Anything else falls
 *  through to the default (`webgl`) so a typo can't silently force a
 *  weird backend. */
function backendFromUrl(): Backend {
  if (typeof window === "undefined") return "webgl";
  const raw = new URLSearchParams(window.location.search).get("backend");
  if (raw && (BACKEND_VALUES as readonly string[]).includes(raw)) {
    return raw as Backend;
  }
  return "webgl";
}

/** What backend the production check-in detector would pick on this
 *  device. Mirrors the logic in PR #332's image-target-detector.ts. */
function runtimeAutoPick(renderer: string | null): Backend {
  if (renderer && /\bMali\b/i.test(renderer)) return "wasm";
  return "webgl";
}

export function App() {
  const [phase, setPhase] = useState<"idle" | "running" | "done" | "error">("idle");
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [report, setReport] = useState<RunReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const reportRef = useRef<RunReport | null>(null);

  const device = useMemo(() => readDeviceFingerprint(), []);
  const deviceLabel = useMemo(() => shortDeviceLabel(device), [device]);
  const backendRequested = useMemo(() => backendFromUrl(), []);
  const runtimeWouldPick = useMemo(
    () => runtimeAutoPick(device.webgl.renderer),
    [device.webgl.renderer],
  );

  const runAll = useCallback(async () => {
    setPhase("running");
    setError(null);
    setReport(null);

    const startedAt = new Date().toISOString();
    const t0 = performance.now();
    const runId = `${deviceLabel}-${Math.random().toString(36).slice(2, 8)}`;

    let manifest;
    try {
      manifest = await fetchManifest();
    } catch (err) {
      setError(`Failed to load manifest: ${err instanceof Error ? err.message : String(err)}`);
      setPhase("error");
      return;
    }
    setProgress({ done: 0, total: manifest.images.length });

    const modelLoadT0 = performance.now();
    try {
      await loadModel(backendRequested);
    } catch (err) {
      setError(`Failed to load MobileNet: ${err instanceof Error ? err.message : String(err)}`);
      setPhase("error");
      return;
    }
    const modelLoadMs = performance.now() - modelLoadT0;

    const results: ImageResult[] = [];

    const EMPTY_EMBEDDING: EmbeddingSummary = {
      vector: [],
      l2Norm: 0,
      fingerprint: "—",
      min: 0,
      max: 0,
      mean: 0,
    };

    for (let i = 0; i < manifest.images.length; i++) {
      const entry = manifest.images[i];
      const tStart = performance.now();

      // Fetch the bytes once so the SHA and the inference both consume
      // the exact same ArrayBuffer. Without this, the page made two
      // separate network requests (fetch + <img src>) which could return
      // different bytes under CDN content negotiation, so the reported
      // SHA wouldn't reliably attest to what MobileNet actually saw.
      let buf: ArrayBuffer | null = null;
      let fetchError: string | null = null;
      try {
        const resp = await fetch(entry.url, { credentials: "omit" });
        if (!resp.ok) throw new Error(`HTTP ${resp.status} ${resp.statusText}`);
        buf = await resp.arrayBuffer();
      } catch (err) {
        fetchError = err instanceof Error ? err.message : String(err);
      }

      if (!buf) {
        results.push({
          id: entry.id,
          url: entry.url,
          category: entry.category,
          notes: entry.notes,
          bytes: { length: 0, sha256: "" },
          embedding: EMPTY_EMBEDDING,
          refMaxCosine: null,
          refLoadedCount: null,
          fetchError,
          inferError: null,
          refError: null,
          durationMs: performance.now() - tStart,
        });
        setProgress({ done: i + 1, total: manifest.images.length });
        continue;
      }

      const sha = await sha256Hex(buf);
      const bytesLen = buf.byteLength;
      const blob = new Blob([buf]);

      let summary: EmbeddingSummary | null = null;
      let inferError: string | null = null;
      try {
        const vec = await embedImage(blob);
        summary = summariseEmbedding(vec);
      } catch (err) {
        inferError = err instanceof Error ? err.message : String(err);
      }

      if (!summary) {
        results.push({
          id: entry.id,
          url: entry.url,
          category: entry.category,
          notes: entry.notes,
          bytes: { length: bytesLen, sha256: sha },
          embedding: EMPTY_EMBEDDING,
          refMaxCosine: null,
          refLoadedCount: null,
          fetchError: null,
          inferError,
          refError: null,
          durationMs: performance.now() - tStart,
        });
        setProgress({ done: i + 1, total: manifest.images.length });
        continue;
      }

      let refMaxCosine: number | null = null;
      let refLoadedCount: number | null = null;
      let refError: string | null = null;
      if (entry.referenceEmbeddingsUrl) {
        try {
          const refResp = await fetch(entry.referenceEmbeddingsUrl, { credentials: "omit" });
          if (!refResp.ok) throw new Error(`HTTP ${refResp.status}`);
          const refFile = (await refResp.json()) as {
            refs: { embedding: number[] }[];
          };
          refLoadedCount = refFile.refs.length;
          let best = -1;
          for (const r of refFile.refs) {
            const sim = cosineSimilarity(summary.vector, r.embedding);
            if (sim > best) best = sim;
          }
          refMaxCosine = best;
        } catch (err) {
          refError = err instanceof Error ? err.message : String(err);
        }
      }

      results.push({
        id: entry.id,
        url: entry.url,
        category: entry.category,
        notes: entry.notes,
        bytes: { length: bytesLen, sha256: sha },
        embedding: summary,
        refMaxCosine,
        refLoadedCount,
        fetchError: null,
        inferError: null,
        refError,
        durationMs: performance.now() - tStart,
      });
      setProgress({ done: i + 1, total: manifest.images.length });
    }

    // Pairwise cosines, computed on the (rounded) summary vectors so the
    // numbers in the JSON match what a reader can recompute themselves.
    const pairwise: { a: string; b: string; cosine: number }[] = [];
    for (let i = 0; i < results.length; i++) {
      for (let j = i + 1; j < results.length; j++) {
        const a = results[i].embedding.vector;
        const b = results[j].embedding.vector;
        if (a.length === 0 || b.length === 0) continue;
        pairwise.push({
          a: results[i].id,
          b: results[j].id,
          cosine: Number(cosineSimilarity(a, b).toFixed(6)),
        });
      }
    }

    const next: RunReport = {
      schema: "ndp61-inference-check@1",
      runId,
      startedAt,
      durationMs: performance.now() - t0,
      device,
      deviceLabel,
      backendRequested,
      tfBackend: getBackendName(),
      runtimeWouldPick,
      modelLoadMs,
      results,
      pairwiseCosine: pairwise,
    };
    setReport(next);
    reportRef.current = next;
    setPhase("done");
  }, [device, deviceLabel, backendRequested, runtimeWouldPick]);

  // Auto-run on first load so the team can just visit the URL and wait.
  // useRef gate makes this safe under <StrictMode> — without it, React
  // intentionally double-invokes effects in dev and we'd kick off two
  // concurrent runAll() executions, skewing the model-load and per-image
  // timings in the resulting report.
  const startedOnceRef = useRef(false);
  useEffect(() => {
    if (startedOnceRef.current) return;
    startedOnceRef.current = true;
    void runAll();
  }, [runAll]);

  const handleCameraSnapshot = useCallback(async (video: HTMLVideoElement) => {
    const current = reportRef.current;
    if (!current) return;
    const vec = await embedVideoFrame(video);
    const summary = summariseEmbedding(vec);
    const id = `camera-snapshot-${current.results.filter((r) => r.id.startsWith("camera-snapshot")).length + 1}`;
    const next: RunReport = {
      ...current,
      results: [
        ...current.results,
        {
          id,
          url: "",
          category: "negative",
          notes: `Live camera frame; videoWidth=${video.videoWidth}, videoHeight=${video.videoHeight}`,
          bytes: { length: 0, sha256: "" },
          embedding: summary,
          refMaxCosine: null,
          refLoadedCount: null,
          fetchError: null,
          inferError: null,
          refError: null,
          durationMs: 0,
        },
      ],
    };
    // Recompute pairwise for the new snapshot only (cheaper than full).
    const newPairs: typeof current.pairwiseCosine = [];
    for (const r of current.results) {
      if (r.embedding.vector.length === 0 || summary.vector.length === 0) continue;
      newPairs.push({
        a: r.id,
        b: id,
        cosine: Number(cosineSimilarity(r.embedding.vector, summary.vector).toFixed(6)),
      });
    }
    next.pairwiseCosine = [...current.pairwiseCosine, ...newPairs];
    setReport(next);
    reportRef.current = next;
  }, []);

  const copyReport = useCallback(async () => {
    if (!report) return;
    const text = JSON.stringify(report, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      alert("Report copied to clipboard. Paste it into the team thread.");
    } catch {
      // navigator.clipboard requires a secure context and a recent user
      // gesture on iOS Safari. Fall back to a textarea so the user can
      // long-press → Select All → Copy manually.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.width = "100vw";
      ta.style.height = "60vh";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      alert("Clipboard API unavailable. The text is selected — Copy then dismiss this dialog.");
      document.body.removeChild(ta);
    }
  }, [report]);

  const downloadReport = useCallback(() => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeId = report.runId.replace(/[^a-zA-Z0-9-]/g, "_");
    a.href = url;
    a.download = `inference-check-${safeId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [report]);

  return (
    <main style={s.shell}>
      <header style={s.header}>
        <h1 style={s.title}>NDP61 Inference Check</h1>
        <p style={s.subtitle}>
          Cross-device MobileNetV2 determinism test for the check-in pipeline.
        </p>
        <div style={s.deviceBadge}>
          <strong>{deviceLabel}</strong> · backend{" "}
          <code>{report?.tfBackend ?? "loading"}</code>
          {report && report.tfBackend !== backendRequested && (
            <span style={s.warn}>
              {" "}(requested <code>{backendRequested}</code> — TF.js fell back)
            </span>
          )}
          {!report && backendRequested !== "webgl" && (
            <span> (forced via <code>?backend={backendRequested}</code>)</span>
          )}
          {" "}· GPU <code>{device.webgl.renderer ?? "?"}</code>
        </div>
        <div style={s.runtimeBadge}>
          Production check-in detector would auto-pick:{" "}
          <code>{runtimeWouldPick}</code>
          {runtimeWouldPick === "wasm" && (
            <span> — Mali GPU detected, runtime forces WASM (see PR #332)</span>
          )}
          {backendRequested === "webgl" && runtimeWouldPick === "wasm" && (
            <span style={s.warn}>
              {" "}· To match production on this device, re-run with{" "}
              <a href="?backend=wasm">?backend=wasm</a>.
            </span>
          )}
        </div>
      </header>

      <section style={s.section}>
        {phase === "running" && (
          <div style={s.progressRow}>
            <span>
              Running… <strong>{progress.done}</strong> / {progress.total}
            </span>
            <div style={s.progressTrack}>
              <div
                style={{
                  ...s.progressFill,
                  width: `${progress.total === 0 ? 0 : (progress.done / progress.total) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {phase === "error" && (
          <div style={s.errorBox}>
            <strong>Run failed.</strong>
            <p style={{ margin: "8px 0 0", whiteSpace: "pre-wrap" }}>{error}</p>
            <button style={s.btnSecondary} onClick={() => void runAll()}>
              Retry
            </button>
          </div>
        )}

        {phase === "done" && report && (
          <>
            <div style={s.actionRow}>
              <button style={s.btnPrimary} onClick={() => void copyReport()}>
                Copy JSON to clipboard
              </button>
              <button style={s.btnSecondary} onClick={downloadReport}>
                Download .json
              </button>
              <button style={s.btnSecondary} onClick={() => setShowCamera((v) => !v)}>
                {showCamera ? "Hide" : "Add"} live camera snapshot
              </button>
              <button style={s.btnGhost} onClick={() => void runAll()}>
                Re-run
              </button>
            </div>

            {/* Backend swap links — full-page reload so the model cache
                is dropped cleanly and TF.js re-initialises on the new
                backend. Hot-swapping in place would force a model
                re-download against a cached promise that's still bound
                to the old backend. */}
            <div style={s.backendSwitcher}>
              Switch backend (reloads page):{" "}
              {BACKEND_VALUES.map((b) => (
                <a
                  key={b}
                  href={b === "webgl" ? "?" : `?backend=${b}`}
                  style={{
                    ...s.backendLink,
                    ...(b === backendRequested ? s.backendLinkActive : {}),
                  }}
                >
                  {b}
                </a>
              ))}
            </div>

            {showCamera && (
              <CameraSnapshot onSnapshot={handleCameraSnapshot} />
            )}

            <h2 style={s.h2}>Results ({report.results.length})</h2>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>ID</th>
                  <th style={s.th}>Category</th>
                  <th style={s.th}>SHA256 (8)</th>
                  <th style={s.th}>L2 norm</th>
                  <th style={s.th}>Fingerprint (×1000)</th>
                  <th style={s.th}>Ref cosine</th>
                  <th style={s.th}>ms</th>
                </tr>
              </thead>
              <tbody>
                {report.results.map((r) => (
                  <tr key={r.id}>
                    <td style={s.td}>
                      <code>{r.id}</code>
                      {r.notes && <div style={s.notes}>{r.notes}</div>}
                      {r.fetchError && <div style={s.errInline}>fetch: {r.fetchError}</div>}
                      {r.inferError && <div style={s.errInline}>infer: {r.inferError}</div>}
                      {r.refError && <div style={s.errInline}>ref: {r.refError}</div>}
                    </td>
                    <td style={s.td}>{r.category}</td>
                    <td style={s.td}>
                      <code>{r.bytes.sha256.slice(0, 8) || "—"}</code>
                    </td>
                    <td style={s.td}>{r.embedding.l2Norm.toFixed(3)}</td>
                    <td style={s.td}>
                      <code>{r.embedding.fingerprint}</code>
                    </td>
                    <td style={s.td}>
                      {r.refMaxCosine === null ? "—" : r.refMaxCosine.toFixed(3)}
                      {r.refLoadedCount !== null && (
                        <div style={s.notes}>vs {r.refLoadedCount} refs</div>
                      )}
                    </td>
                    <td style={s.td}>{r.durationMs.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {report.pairwiseCosine.length > 0 && (
              <>
                <h2 style={s.h2}>Pairwise cosine</h2>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>A</th>
                      <th style={s.th}>B</th>
                      <th style={s.th}>cos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.pairwiseCosine.map((p, i) => (
                      <tr key={i}>
                        <td style={s.td}>
                          <code>{p.a}</code>
                        </td>
                        <td style={s.td}>
                          <code>{p.b}</code>
                        </td>
                        <td style={s.td}>{p.cosine.toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </section>

      <footer style={s.footer}>
        <p>
          schema <code>ndp61-inference-check@1</code> · runId{" "}
          <code>{report?.runId ?? "—"}</code>
        </p>
      </footer>
    </main>
  );
}

const s: Record<string, React.CSSProperties> = {
  shell: {
    maxWidth: 880,
    margin: "0 auto",
    padding: "16px 16px 64px",
  },
  header: {
    borderBottom: "1px solid #e0e0e0",
    paddingBottom: 12,
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
    color: "#c8102e",
  },
  subtitle: {
    fontSize: 14,
    color: "#555",
    margin: "4px 0 0",
  },
  deviceBadge: {
    marginTop: 8,
    fontSize: 12,
    color: "#444",
    background: "#fff",
    border: "1px solid #ddd",
    padding: "6px 10px",
    borderRadius: 6,
    overflowX: "auto",
    whiteSpace: "nowrap",
  },
  runtimeBadge: {
    marginTop: 6,
    fontSize: 12,
    color: "#444",
    background: "#fafafa",
    border: "1px solid #eee",
    padding: "6px 10px",
    borderRadius: 6,
    overflowX: "auto",
    whiteSpace: "nowrap",
  },
  warn: {
    color: "#a06800",
  },
  backendSwitcher: {
    margin: "0 0 16px",
    padding: "8px 12px",
    background: "#fafafa",
    border: "1px solid #eee",
    borderRadius: 6,
    fontSize: 12,
    color: "#666",
  },
  backendLink: {
    display: "inline-block",
    margin: "0 4px",
    padding: "2px 8px",
    borderRadius: 4,
    border: "1px solid #ccc",
    background: "#fff",
    color: "#444",
    textDecoration: "none",
    fontFamily: "ui-monospace, SFMono-Regular, monospace",
  },
  backendLinkActive: {
    background: "#c8102e",
    color: "#fff",
    borderColor: "#c8102e",
  },
  section: {
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    padding: 16,
  },
  progressRow: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  progressTrack: {
    width: "100%",
    height: 8,
    background: "#eee",
    borderRadius: 4,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "#c8102e",
    transition: "width 0.2s linear",
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 16,
  },
  btnPrimary: {
    minHeight: 40,
    padding: "0 14px",
    borderRadius: 6,
    border: "none",
    background: "#c8102e",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnSecondary: {
    minHeight: 40,
    padding: "0 14px",
    borderRadius: 6,
    border: "1px solid #c8102e",
    background: "#fff",
    color: "#c8102e",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnGhost: {
    minHeight: 40,
    padding: "0 14px",
    borderRadius: 6,
    border: "1px solid #aaa",
    background: "#fff",
    color: "#333",
    fontSize: 14,
    cursor: "pointer",
  },
  h2: {
    fontSize: 15,
    fontWeight: 700,
    margin: "16px 0 8px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    textAlign: "left",
    padding: "6px 8px",
    borderBottom: "2px solid #ddd",
    background: "#fafafa",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "6px 8px",
    borderBottom: "1px solid #eee",
    verticalAlign: "top",
  },
  notes: {
    fontSize: 11,
    color: "#666",
    marginTop: 2,
  },
  errInline: {
    fontSize: 11,
    color: "#c0392b",
    marginTop: 2,
  },
  errorBox: {
    background: "#fdf0ef",
    border: "1px solid #f5c8c4",
    borderRadius: 6,
    padding: 12,
    color: "#a92e1f",
  },
  footer: {
    marginTop: 16,
    fontSize: 11,
    color: "#888",
  },
};
